import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { CreditCard, Building2, Check, Loader2, ArrowRight, Shield, Zap, Globe, Info, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSearchParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Transaction limits by account type
const LIMITS = {
  personal: {
    card: { min: 50, max: 25000, label: 'Card Payment' },
    transfer: { min: 100, max: 1000000, label: 'Bank Transfer' }
  },
  business: {
    card: { min: 100, max: 50000, label: 'Card Payment' },
    transfer: { min: 500, max: 5000000, label: 'Bank Transfer' }
  }
};

const Banking = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [amount, setAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  // Determine account type (business or personal)
  const accountType = user?.is_business ? 'business' : 'personal';
  const limits = LIMITS[accountType][paymentMethod];

  // Theme colors
  const cardBg = isDark ? 'bg-[#111111]' : 'bg-white';
  const cardBorder = isDark ? 'border-white/10' : 'border-gray-200';
  const textPrimary = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-white/60' : 'text-gray-600';
  const inputBg = isDark ? 'bg-white/5' : 'bg-gray-50';

  const checkPaymentStatus = React.useCallback(async (sessionId, attempt = 0) => {
    if (attempt >= 5) {
      toast.error('Payment session expired');
      return;
    }

    setIsCheckingStatus(true);
    try {
      const response = await axios.get(`${API}/banking/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Funds added to your account.');
        window.history.replaceState({}, '', '/banking');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        window.history.replaceState({}, '', '/banking');
        return;
      }

      setTimeout(() => checkPaymentStatus(sessionId, attempt + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Payment verification failed');
    } finally {
      setIsCheckingStatus(false);
    }
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams, checkPaymentStatus]);

  const handleBuy = async () => {
    const numAmount = parseFloat(amount);
    
    if (!numAmount || numAmount < limits.min) {
      toast.error(`Minimum amount is $${limits.min.toLocaleString()}`);
      return;
    }
    
    if (numAmount > limits.max) {
      toast.error(`Maximum amount is $${limits.max.toLocaleString()}`);
      return;
    }

    setIsProcessing(true);

    try {
      const response = await axios.post(`${API}/banking/buy-custom`, {
        amount: numAmount,
        payment_method: paymentMethod,
        email: user?.email
      });

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success('Transfer initiated! Follow the instructions sent to your email.');
        setAmount('');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to process payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const quickAmounts = paymentMethod === 'card' 
    ? [500, 1000, 5000, 10000, 25000]
    : [10000, 50000, 100000, 500000, 1000000];

  if (isCheckingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
          <p className={`text-lg ${textPrimary}`}>Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="banking-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className={`text-2xl md:text-3xl font-bold ${textPrimary} mb-2`}>Add Funds</h1>
          <p className={`${textMuted} text-sm`}>
            Deposit funds to your Onerik account instantly
          </p>
        </div>

        {/* Account Type Badge */}
        <div className="flex justify-center mb-6">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
            accountType === 'business' 
              ? 'bg-purple-500/10 border border-purple-500/20' 
              : 'bg-blue-500/10 border border-blue-500/20'
          }`}>
            <Building2 className={`w-4 h-4 ${accountType === 'business' ? 'text-purple-400' : 'text-blue-400'}`} />
            <span className={`text-sm font-medium ${accountType === 'business' ? 'text-purple-400' : 'text-blue-400'}`}>
              {accountType === 'business' ? 'Business Account' : 'Personal Account'}
            </span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Payment Form */}
          <div className={`${cardBg} border ${cardBorder} rounded-2xl p-6`}>
            {/* Payment Method Toggle */}
            <div className="mb-6">
              <label className={`text-sm font-medium ${textMuted} mb-3 block`}>Payment Method</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    paymentMethod === 'card'
                      ? 'border-purple-500 bg-purple-500/10'
                      : `${cardBorder} hover:border-purple-500/50`
                  }`}
                >
                  <CreditCard className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'card' ? 'text-purple-400' : textMuted}`} />
                  <p className={`text-sm font-medium ${textPrimary}`}>Card</p>
                  <p className={`text-xs ${textMuted}`}>Instant</p>
                </button>
                <button
                  onClick={() => setPaymentMethod('transfer')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    paymentMethod === 'transfer'
                      ? 'border-purple-500 bg-purple-500/10'
                      : `${cardBorder} hover:border-purple-500/50`
                  }`}
                >
                  <Building2 className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'transfer' ? 'text-purple-400' : textMuted}`} />
                  <p className={`text-sm font-medium ${textPrimary}`}>Bank Transfer</p>
                  <p className={`text-xs ${textMuted}`}>1-2 days</p>
                </button>
              </div>
            </div>

            {/* Amount Input */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <label className={`text-sm font-medium ${textMuted}`}>Amount (USD)</label>
                <span className={`text-xs ${textMuted}`}>
                  Limit: ${limits.min.toLocaleString()} - ${limits.max.toLocaleString()}
                </span>
              </div>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl ${textMuted}`}>$</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min={limits.min}
                  max={limits.max}
                  className={`w-full ${inputBg} border ${cardBorder} rounded-xl h-14 pl-10 pr-4 text-2xl font-bold ${textPrimary} placeholder:${textMuted} focus:outline-none focus:border-purple-500 transition-colors`}
                />
              </div>
            </div>

            {/* Quick Amount Buttons */}
            <div className="mb-6">
              <label className={`text-sm font-medium ${textMuted} mb-2 block`}>Quick Select</label>
              <div className="flex flex-wrap gap-2">
                {quickAmounts.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setAmount(amt.toString())}
                    disabled={amt > limits.max}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      amount === amt.toString()
                        ? 'bg-purple-500 text-white'
                        : `${inputBg} ${textMuted} hover:bg-purple-500/10 hover:text-purple-400`
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                  >
                    ${amt >= 1000000 ? `${amt / 1000000}M` : amt >= 1000 ? `${amt / 1000}K` : amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Fee Info */}
            <div className={`${inputBg} rounded-xl p-4 mb-6`}>
              <div className="flex justify-between mb-2">
                <span className={textMuted}>Amount</span>
                <span className={textPrimary}>${parseFloat(amount || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between mb-2">
                <span className={textMuted}>Fee ({paymentMethod === 'card' ? '2.9%' : '0.5%'})</span>
                <span className={textMuted}>
                  ${((parseFloat(amount || 0) * (paymentMethod === 'card' ? 0.029 : 0.005))).toFixed(2)}
                </span>
              </div>
              <div className={`border-t ${cardBorder} pt-2 mt-2`}>
                <div className="flex justify-between">
                  <span className={`font-medium ${textPrimary}`}>You receive</span>
                  <span className={`font-bold text-lg ${textPrimary}`}>
                    ${(parseFloat(amount || 0) - (parseFloat(amount || 0) * (paymentMethod === 'card' ? 0.029 : 0.005))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleBuy}
              disabled={isProcessing || !amount}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold py-4 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Continue to Payment
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>

          {/* Right: Info Cards */}
          <div className="space-y-4">
            {/* Limits Card */}
            <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
              <h3 className={`font-semibold ${textPrimary} mb-4 flex items-center gap-2`}>
                <Info className="w-5 h-5 text-purple-500" />
                Transaction Limits
              </h3>
              
              <div className="space-y-4">
                {/* Card Limits */}
                <div className={`${inputBg} rounded-xl p-4`}>
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="w-5 h-5 text-purple-400" />
                    <span className={`font-medium ${textPrimary}`}>Card Payments</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className={textMuted}>Personal</p>
                      <p className={`font-semibold ${textPrimary}`}>Up to $25,000</p>
                    </div>
                    <div>
                      <p className={textMuted}>Business</p>
                      <p className={`font-semibold ${textPrimary}`}>Up to $50,000</p>
                    </div>
                  </div>
                </div>

                {/* Transfer Limits */}
                <div className={`${inputBg} rounded-xl p-4`}>
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="w-5 h-5 text-blue-400" />
                    <span className={`font-medium ${textPrimary}`}>Bank Transfers</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className={textMuted}>Personal</p>
                      <p className={`font-semibold ${textPrimary}`}>Up to $1,000,000</p>
                    </div>
                    <div>
                      <p className={textMuted}>Business</p>
                      <p className={`font-semibold ${textPrimary}`}>Up to $5,000,000</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Card */}
            <div className={`${cardBg} border ${cardBorder} rounded-2xl p-5`}>
              <h3 className={`font-semibold ${textPrimary} mb-4`}>Why Onerik?</h3>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className={`font-medium ${textPrimary} text-sm`}>Instant Processing</p>
                    <p className={`text-xs ${textMuted}`}>Card payments credited immediately</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-4 h-4 text-purple-500" />
                  </div>
                  <div>
                    <p className={`font-medium ${textPrimary} text-sm`}>Bank-Grade Security</p>
                    <p className={`text-xs ${textMuted}`}>256-bit SSL encryption</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Globe className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <p className={`font-medium ${textPrimary} text-sm`}>Global Coverage</p>
                    <p className={`text-xs ${textMuted}`}>Accept payments worldwide</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Upgrade to Business */}
            {accountType === 'personal' && (
              <div className={`${isDark ? 'bg-gradient-to-br from-purple-600/20 to-blue-600/20 border-purple-500/30' : 'bg-gradient-to-br from-purple-100 to-blue-100 border-purple-300'} border rounded-2xl p-5`}>
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Need Higher Limits?</h3>
                <p className={`${isDark ? 'text-white/70' : 'text-gray-600'} text-sm mb-4`}>
                  Upgrade to a Business account for limits up to $5,000,000 per transfer.
                </p>
                <button 
                  onClick={() => navigate('/get-started?upgrade=business')}
                  className="flex items-center gap-2 text-purple-600 font-medium text-sm hover:text-purple-500 transition-colors"
                >
                  Upgrade to Business
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`mt-6 text-center text-xs ${textMuted}`}>
          <p>Payments secured by Stripe • Funds protected up to $250,000 FDIC</p>
        </div>
      </div>
    </div>
  );
};

export default Banking;
